/**
 * **Whether a desk key belongs to the screen or to the widget the keyboard is sitting on.**
 *
 * The desk listens at the document, because a decision is about the CARD and not about whatever
 * currently holds focus. That only works if the screen can say, for each key, whether the focused
 * widget already means something by it.
 *
 * ── Why this is a module and not four lines in the listener ──────────────────────────────────
 * The first version asked one question — "is focus inside anything that owns arrows?" — and applied
 * the answer to EVERY key. The owner reported, on 19 September, that after working the screen with
 * the mouse the keys stopped answering at all: click a scope radio and the whole keyboard went
 * quiet, including `Z`, because an `<input>` matched the inert list; click a row and `Enter`
 * returned early, because a row is a `<button>` and every button was assumed to answer Enter for
 * itself. One boolean cannot carry two different rules, and a rule with an exception in it belongs
 * somewhere a table can be written against it.
 *
 * ── The rule ────────────────────────────────────────────────────────────────────────────────
 * A key is the widget's, not the screen's, only when the FOCUSED WIDGET ITSELF uses that key:
 *
 * · Text entry and an open modal own the whole keyboard while they are up.
 * · A radio group, a menu, a listbox, a slider, a spin button, a `<video>` and a chart whose
 *   readings are walked with the keyboard own the ARROWS. They do not own `Z`, and nothing about
 *   them makes `Z` ambiguous.
 * · A button or a link owns `Enter` and `Space` — activating it would otherwise take the act twice,
 *   once on the control the trainer chose and once on the card's primary.
 * · An INDEX ROW is the exception that made this a table. It is a button, so the platform activates
 *   it on Enter, but selecting the row the keyboard is already on is a no-op the trainer reads as
 *   "Enter does nothing". On a row, `Enter` runs the card's primary and the arrows move the
 *   selection — the row is a place to stand on the queue, not a control with an act of its own.
 *
 * Everywhere else, `Z` and the four arrows are the screen's.
 *
 * Pure: no React, no DOM. The hook classifies the event's target into a `FocusKind`; this decides.
 */

/** What the keyboard is sitting on, as far as the desk's keys are concerned. */
export type FocusKind =
  /** The page itself — `<body>`, a heading, a paragraph. Nothing competes. */
  | 'page'
  /** An input that takes typing, a textarea, a contenteditable. The session note lives here. */
  | 'text'
  /** An open dialog or alert dialog. */
  | 'modal'
  /** A widget whose own keyboard contract is the arrows: radio group, menu, listbox, slider, video,
   * and a card's readings plot. */
  | 'arrows'
  /** A button, a link, a switch — anything the platform activates on Enter or Space. */
  | 'control'
  /** One of the queue index's rows. A control by markup, a cursor by behaviour. */
  | 'indexRow';

/** The keys the desk claims. `⇧←` is `ArrowLeft` with the shift flag, decided by the caller. */
export type DeskKey = 'ArrowLeft' | 'ArrowRight' | 'ArrowUp' | 'ArrowDown' | 'Enter' | 'Z';

const ARROWS: ReadonlySet<DeskKey> = new Set<DeskKey>([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
]);

/**
 * Does the screen handle this key while focus is on a widget of this kind?
 *
 * `false` means the key is left entirely alone — not swallowed, not prevented — so the widget's own
 * behaviour happens exactly as the platform wrote it.
 */
export function deskKeyIsOurs(focus: FocusKind, key: DeskKey): boolean {
  switch (focus) {
    case 'text':
    case 'modal':
      return false;
    case 'arrows':
      return !ARROWS.has(key) && key !== 'Enter';
    case 'control':
      return key !== 'Enter';
    case 'indexRow':
    case 'page':
      return true;
  }
}
